import React from 'react';
import { Icon } from '@iconify/react';
import {
  LoyaltyActiveSystem,
  LoyaltyParticipantItem,
  LoyaltyParticipantsResponse,
} from '@/types/loyalty';
import { cn } from '@/lib/utils';

interface Props {
  activeSystem: LoyaltyActiveSystem;
  data: LoyaltyParticipantsResponse | null;
  loading: boolean;
  searchQuery: string;
  onSearchChange: (val: string) => void;
  page: number;
  onPageChange: (newPage: number) => void;
  onOpenAddMember: () => void;
  onOpenAdjust: (participant: LoyaltyParticipantItem) => void;
  isStaff?: boolean;
}

export const ParticipantsTab: React.FC<Props> = ({
  activeSystem,
  data,
  loading,
  searchQuery,
  onSearchChange,
  page,
  onPageChange,
  onOpenAddMember,
  onOpenAdjust,
  isStaff = false,
}) => {
  const participants = data?.participants || [];
  const totalItems = data?.pagination?.total_items || 0;
  const totalPages = data?.pagination?.total_pages || 1;
  const limit = data?.pagination?.limit || 10;

  const renderPaginationButtons = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('...');
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }
      if (page < totalPages - 2) pages.push('...');
      if (!pages.includes(totalPages)) pages.push(totalPages);
    }

    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D5D5D5] bg-white text-[#1F1F1F] transition-colors hover:bg-[#F6F6F6] disabled:cursor-not-allowed disabled:opacity-45"
        >
          <Icon icon="ph:caret-left" className="h-4 w-4" />
        </button>

        {pages.map((p, idx) =>
          p === '...' ? (
            <span key={`dots-${idx}`} className="px-1.5 text-[14px] text-[#8C8C8C]">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(Number(p))}
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-full text-[14px] font-semibold transition-colors',
                p === page
                  ? 'bg-[#FC480D] text-white'
                  : 'border border-[#D5D5D5] bg-white text-[#1F1F1F] hover:bg-[#F6F6F6]'
              )}
            >
              {p}
            </button>
          )
        )}

        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D5D5D5] bg-white text-[#1F1F1F] transition-colors hover:bg-[#F6F6F6] disabled:cursor-not-allowed disabled:opacity-45"
        >
          <Icon icon="ph:caret-right" className="h-4 w-4" />
        </button>
      </div>
    );
  };

  return (
    <div className="rounded-[16px] border border-[#E0E6ED] bg-white p-6 shadow-none space-y-5">
      {/* Search and Action Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* Search bar 40px height per design system */}
        <div className="relative w-full sm:w-[280px]">
          <Icon
            icon="ph:magnifying-glass"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#8C8C8C]"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Cari pelanggan..."
            className="w-full h-10 rounded-full border border-[#E0E6ED] bg-white pl-11 pr-4 text-[14px] text-[#1F1F1F] placeholder:text-[#8C8C8C] shadow-sm focus:border-[#FC480D] focus:outline-none"
          />
        </div>

        {activeSystem === 'membership' && (
          <button
            type="button"
            onClick={onOpenAddMember}
            className="inline-flex items-center justify-center gap-1.5 h-10 px-[18px] rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white text-[14px] font-semibold transition-colors shrink-0"
          >
            <Icon icon="ph:user-plus" className="w-[14px] h-[14px]" />
            Tambah Member
          </button>
        )}
      </div>

      {/* Participants Table Container */}
      {loading ? (
        <div className="py-20 text-center">
          <Icon icon="ph:spinner" className="w-8 h-8 text-[#FC480D] animate-spin mx-auto mb-2" />
          <p className="text-[13px] text-[#8C8C8C]">Memuat data peserta loyalty...</p>
        </div>
      ) : participants.length === 0 ? (
        <div className="rounded-[16px] border border-dashed border-[#D5D5D5] py-16 text-center">
          <Icon icon="ph:users" className="w-10 h-10 text-[#8C8C8C] mx-auto mb-2" />
          <h5 className="text-[14px] font-bold text-[#0E1726]">Belum ada peserta terdaftar</h5>
          <p className="text-[13px] text-[#8C8C8C] mt-1 max-w-sm mx-auto">
            {searchQuery
              ? `Tidak ditemukan peserta dengan kata kunci "${searchQuery}".`
              : activeSystem === 'membership'
              ? 'Belum ada member terdaftar. Klik "Tambah Member" untuk mendaftarkan pelanggan walk-in.'
              : 'Peserta akan otomatis terdata saat pelanggan melakukan booking atau transaksi di arena.'}
          </p>
        </div>
      ) : (
        <div className="rounded-[16px] border border-[#E0E6ED] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left font-['Inter']">
              <thead>
                <tr className="bg-[#F6F6F6] text-[13px] font-semibold text-[#1F1F1F] border-b border-[#E0E6ED]">
                  <th className="py-3.5 px-5 font-semibold">Pelanggan</th>
                  {activeSystem === 'points' && (
                    <>
                      <th className="py-3.5 px-5 font-semibold">Saldo Poin</th>
                      <th className="py-3.5 px-5 font-semibold">Total Didapat</th>
                      <th className="py-3.5 px-5 font-semibold">Total Ditukar</th>
                      <th className="py-3.5 px-5 font-semibold">Terakhir Aktif</th>
                    </>
                  )}
                  {activeSystem === 'membership' && (
                    <>
                      <th className="py-3.5 px-5 font-semibold">Periode Aktif</th>
                      <th className="py-3.5 px-5 font-semibold">Sisa Hari</th>
                      <th className="py-3.5 px-5 font-semibold">Status</th>
                    </>
                  )}
                  {activeSystem === 'stamp' && (
                    <>
                      <th className="py-3.5 px-5 font-semibold">Progress Stamp</th>
                      <th className="py-3.5 px-5 font-semibold">Hadiah (Klaim / Digunakan)</th>
                      <th className="py-3.5 px-5 font-semibold">Terakhir Aktif</th>
                    </>
                  )}
                  <th className="py-3.5 px-5 font-semibold text-right">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E0E6ED] text-[13px] text-[#1F1F1F] bg-white">
                {participants.map((p) => {
                  const target = p.stamp_target || 10;
                  const current = p.stamp_current || 0;
                  const progressPct = Math.min(100, Math.round((current / target) * 100));

                  return (
                    <tr key={p.customer_id} className="hover:bg-[#F6F6F6] transition-colors">
                      <td className="py-3.5 px-5">
                        <div className="font-medium text-[#0E1726]">{p.name}</div>
                        <div className="text-[12px] text-[#8C8C8C] font-mono">
                          {p.formatted_phone || p.phone || '-'}
                        </div>
                      </td>

                      {activeSystem === 'points' && (
                        <>
                          <td className="py-3.5 px-5 font-semibold text-[#FC480D]">
                            {(p.points_balance || 0).toLocaleString('id-ID')} poin
                          </td>
                          <td className="py-3.5 px-5 text-[#1F1F1F]">
                            {(p.points_total_earned || 0).toLocaleString('id-ID')}
                          </td>
                          <td className="py-3.5 px-5 text-[#8C8C8C]">
                            {(p.points_total_redeemed || 0).toLocaleString('id-ID')}
                          </td>
                          <td className="py-3.5 px-5 text-[#8C8C8C]">
                            {p.last_activity_at || '-'}
                          </td>
                        </>
                      )}

                      {activeSystem === 'membership' && (
                        <>
                          <td className="py-3.5 px-5">
                            <div className="text-[#1F1F1F]">
                              {p.membership_start_date} s/d {p.membership_end_date}
                            </div>
                            {p.membership_total_renewals ? (
                              <div className="text-[11px] text-[#8C8C8C]">
                                Diperpanjang {p.membership_total_renewals}x
                              </div>
                            ) : null}
                          </td>
                          <td className="py-3.5 px-5 font-semibold text-[#0E1726]">
                            {p.membership_days_remaining !== undefined
                              ? p.membership_days_remaining > 0
                                ? `${p.membership_days_remaining} hari`
                                : '0 hari (Habis)'
                              : '-'}
                          </td>
                          <td className="py-3.5 px-5">
                            {p.membership_status === 'active' && (
                              <span className="inline-flex items-center rounded-full bg-[#0BCF46]/12 px-2.5 py-[3px] text-[12px] font-medium text-[#088a2f] border border-[#0BCF46]">
                                Active
                              </span>
                            )}
                            {p.membership_status === 'expiring' && (
                              <span className="inline-flex items-center rounded-full bg-[#FFF5EE] px-2.5 py-[3px] text-[12px] font-medium text-[#FC480D] border border-[#FC480D]">
                                Segera Berakhir
                              </span>
                            )}
                            {p.membership_status === 'expired' && (
                              <span className="inline-flex items-center rounded-full bg-[#F5F6F7] px-2.5 py-[3px] text-[12px] font-medium text-[#8C8C8C] border border-[#E0E6ED]">
                                Nonactive
                              </span>
                            )}
                          </td>
                        </>
                      )}

                      {activeSystem === 'stamp' && (
                        <>
                          <td className="py-3.5 px-5">
                            <div className="flex items-center gap-2.5">
                              <span className="font-semibold text-[#0E1726]">
                                {current} / {target}
                              </span>
                              <div className="w-20 bg-[#E0E6ED] h-1.5 rounded-full overflow-hidden">
                                <div
                                  className="bg-[#FC480D] h-full rounded-full transition-all duration-300"
                                  style={{ width: `${progressPct}%` }}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="py-3.5 px-5 text-[#1F1F1F]">
                            <span className="font-semibold text-[#0E1726]">{p.stamp_rewards_earned || 0}</span> jam gratis
                            <span className="text-[#8C8C8C] text-[12px]"> ({p.stamp_rewards_used || 0} dipakai)</span>
                          </td>
                          <td className="py-3.5 px-5 text-[#8C8C8C]">
                            {p.last_activity_at || '-'}
                          </td>
                        </>
                      )}

                      <td className="py-3.5 px-5 text-right">
                        {!isStaff ? (
                          <button
                            type="button"
                            onClick={() => onOpenAdjust(p)}
                            className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-[#D5D5D5] bg-white text-[#1F1F1F] hover:bg-[#F6F6F6] text-[13px] font-semibold transition-colors"
                          >
                            <Icon icon="ph:faders" className="w-[14px] h-[14px] text-[#8C8C8C]" />
                            Sesuaikan
                          </button>
                        ) : (
                          <span className="text-[12px] text-[#8C8C8C]">Read-only</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination Footer */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2">
        <p className="text-[14px] text-[#8C8C8C]">
          Showing{' '}
          <span className="text-[#1F1F1F]">
            {participants.length === 0 ? 0 : (page - 1) * limit + 1}
          </span>{' '}
          -{' '}
          <span className="text-[#1F1F1F]">
            {Math.min(page * limit, totalItems)}
          </span>{' '}
          of <span className="text-[#1F1F1F]">{totalItems.toLocaleString('id-ID')}</span>
        </p>
        <div>{renderPaginationButtons()}</div>
      </div>
    </div>
  );
};

export default ParticipantsTab;
